// Tenant-specific UI restrictions.
// Add a hostname to RESTRICTED_TENANTS to apply all restricted-tenant
// hiding rules to that tenant. Match is exact on window.location.hostname.
const RESTRICTED_TENANTS = [
  "teamupcorp.reachiq.ai",
  "yvcio.reachiq.ai",
  "breezydev.reachiq.ai",
  "workdigital.reachiq.ai",
  "blazent.reachiq.ai",
  "demo.reachiq.ai",
  "networkingdynamics.reachiq.ai",
];

// Restricted tenants where the DASHBOARD ONLY should behave as unrestricted
// (i.e. show the same dashboard as get-eturns: full KPI strip, no extra
// "Emails Sent" hero card, and the CSV-Enrichment sidebar item visible). Every
// OTHER restricted-tenant rule (Profile, ICP wizard, Billing, Create Campaign,
// etc.) still applies to these tenants unchanged.
// ⚠️ Sequence + Templates are NOT in that carve-out any more: this flag used to
// keep them visible in the Marketing menu too, which meant workdigital/blazent
// were "restricted" everywhere except the one menu that mattered most
// (fixed 2026-08-14 in DashboardLayout).
// These MUST be a subset of RESTRICTED_TENANTS.
const DASHBOARD_UNRESTRICTED_TENANTS = [
  "workdigital.reachiq.ai",
  "blazent.reachiq.ai",
  "networkingdynamics.reachiq.ai",
];

// Tenants that get the full restricted treatment EXCEPT for a couple of
// tweaks (see isRepliesDashboardTenant / isAutoTopUpEnabledTenant below).
// These are a subset of RESTRICTED_TENANTS.
const REPLIES_DASHBOARD_TENANTS = ["yvcio.reachiq.ai", "breezydev.reachiq.ai"];

// Tenants that get the campaign "tenant prompt"-only behaviour: in Create
// Campaign these default the sequence type to "tenantPrompt", hide the
// sequence-type selector, and therefore only ever hit the tenantPrompts API
// (never the regular sequenceList API).
//   - get-eturns.reachiq.ai gets ONLY this campaign behaviour and NONE of the
//     other restricted-tenant hiding rules, so it is kept OUT of
//     RESTRICTED_TENANTS.
//   - workdigital.reachiq.ai + blazent.reachiq.ai get this campaign behaviour
//     AND the full restricted-tenant treatment, so they are listed in BOTH this
//     array and RESTRICTED_TENANTS above. Their DASHBOARD is the one exception,
//     made unrestricted via DASHBOARD_UNRESTRICTED_TENANTS above. (For a tenant
//     already in RESTRICTED_TENANTS this listing is redundant — both call-sites
//     OR the two checks — it is kept for parity/explicitness.)
const TENANT_PROMPT_ONLY_TENANTS = [
  "get-eturns.reachiq.ai",
  "workdigital.reachiq.ai",
  "blazent.reachiq.ai",
  "networkingdynamics.reachiq.ai",
];

// Tenants that keep the normal (unrestricted) UI but hide a few menu items:
// Settings > Account Setup > Users, Settings > Data Management > Forms, and
// Marketing > Sequence + Templates. NOT part of RESTRICTED_TENANTS, so none
// of the other restricted-tenant rules apply.
const LIMITED_MENU_TENANTS = ["bestinbarter.reachiq.ai"];

// Tenants whose dashboard "Outreach by Channel" chart shows the Email series
// only (no Calls / Tasks bars). Display-only: the campaign-insights API and the
// KPI tiles are untouched. Match is exact on window.location.hostname.
const EMAIL_ONLY_OUTREACH_CHART_TENANTS = ["blazent.reachiq.ai"];

// Restricted tenants where specific logged-in users must still see the
// Daily Activity Reports section in Settings (ProfileScreen). Keyed by
// hostname; values are lower-cased user emails.
// Emails MUST be lower-cased — isActivityReportsAllowedUser lower-cases the
// incoming address before comparing, it does not normalise this list.
// blazent: both tenant users are listed so the whole tenant is covered. In
// practice that means nathan.young — mnicholas is role_access:'admin-sdr', so
// Settings is hidden from him and he never reaches this screen. A THIRD blazent
// user would need adding here explicitly; this allow-list is per-email, not
// per-tenant.
const ACTIVITY_REPORTS_ALLOWED_USERS = {
  "workdigital.reachiq.ai": ["abrar.alam@workdigital.com"],
  "blazent.reachiq.ai": [
    "nathan.young@blazent.com",
    "mnicholas@blazent.com",
  ],
  "networkingdynamics.reachiq.ai": [
    "maria@networkingdynamics.com",
    "saurav.barua@networkingdynamics.com",
  ],
};

export const isActivityReportsAllowedUser = (email) => {
  const allowed = ACTIVITY_REPORTS_ALLOWED_USERS[window.location.hostname];
  return !!allowed && !!email && allowed.includes(String(email).toLowerCase());
};

export const isRestrictedTenant = () => {
  return RESTRICTED_TENANTS.includes(window.location.hostname);
};

// True for tenants that hide Users, Forms, Sequence and Templates only (see
// LIMITED_MENU_TENANTS). Call-sites OR this with isRestrictedTenant().
export const isLimitedMenuTenant = () => {
  return LIMITED_MENU_TENANTS.includes(window.location.hostname);
};

// True for restricted tenants whose DASHBOARD ONLY should render as unrestricted
// (same dashboard as get-eturns). Use this ONLY in dashboard/sidebar code to
// decide dashboard visibility; keep using isRestrictedTenant() everywhere else.
export const isDashboardUnrestrictedTenant = () => {
  return DASHBOARD_UNRESTRICTED_TENANTS.includes(window.location.hostname);
};

// Restricted tenants where the dashboard hero row swaps the "Total Outreach"
// card for a clickable "Total Replies" card.
export const isRepliesDashboardTenant = () => {
  return REPLIES_DASHBOARD_TENANTS.includes(window.location.hostname);
};

// Restricted tenants where Auto-Top-up stays enabled (not greyed out).
// Currently this lines up exactly with the replies-dashboard tenants.
export const isAutoTopUpEnabledTenant = () => {
  return REPLIES_DASHBOARD_TENANTS.includes(window.location.hostname);
};

// Tenants where Create Campaign should use the "tenant prompt"-only flow
// (tenantPrompt default + hidden selector + only the tenantPrompts API) WITHOUT
// pulling in any other restricted-tenant behaviour. Used only by the campaign
// creation flow. Note this is intentionally independent of isRestrictedTenant().
export const isTenantPromptOnlyTenant = () => {
  return TENANT_PROMPT_ONLY_TENANTS.includes(window.location.hostname);
};

// True for tenants whose "Outreach by Channel" dashboard chart should render
// only the Email series (see EMAIL_ONLY_OUTREACH_CHART_TENANTS).
export const isEmailOnlyOutreachChartTenant = () => {
  return EMAIL_ONLY_OUTREACH_CHART_TENANTS.includes(window.location.hostname);
};
